import { useLanguage } from "@/context/LanguageContext";
import { buildWaLink } from "@/lib/whatsapp";
import { ArrowUpRight, Award } from "lucide-react";

const Hero = ({ simplified = false }) => {
  const { t, lang, isRTL } = useLanguage();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(255,90,0,0.10),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-white border border-[#E2DFD2] text-xs font-bold uppercase tracking-[0.18em] text-[#0B1320]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" />
              {t("hero.eyebrow")}
            </div>

            <h1
              className="mt-6 font-black tracking-tight leading-[0.98] text-[#0B1320]"
              style={{
                fontSize: "clamp(2.5rem, 6.5vw, 5rem)",
                fontFamily:
                  lang === "ar" ? "Tajawal, sans-serif" : "Outfit, sans-serif",
              }}
              data-testid="hero-title"
            >
              <span className="block">{t("hero.titleLine1")}</span>
              <span className="block text-[#0B1320]/40">
                {t("hero.titleLine2")}
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-lg md:text-xl leading-relaxed text-[#475569]">
              {t("hero.subtitle")}
            </p>

            {/* Family legacy pill */}
            <div
              data-testid="hero-legacy"
              className="mt-6 inline-flex items-center gap-3 py-2.5 pe-5 ps-2.5 rounded-full bg-white border border-[#E2DFD2]"
            >
              <span className="h-8 w-8 rounded-full bg-[#0B1320] text-[#FF5A00] grid place-items-center shrink-0">
                <Award size={16} strokeWidth={2.5} />
              </span>
              <span className="flex flex-col leading-tight">
                <span
                  data-testid="hero-legacy-label"
                  className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF5A00]"
                >
                  {t("hero.legacy_label")}
                </span>
                <span className="text-sm font-bold text-[#0B1320]">
                  {t("hero.legacy")}
                </span>
              </span>
            </div>

            <div className="mt-9">
              <a
                data-testid="hero-primary-cta"
                href={buildWaLink(t("whatsapp.msg_default"))}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[#25D366] text-white text-base font-bold hover:brightness-110 transition-all shadow-[0_10px_30px_rgba(37,211,102,0.30)] hover:-translate-y-0.5"
              >
                {t("hero.cta_primary")}
                <ArrowUpRight
                  size={18}
                  className={isRTL ? "scale-x-[-1]" : ""}
                />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(11,19,32,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80"
                alt="Home"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B1320]/60 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
